import React, { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Show an eye icon to toggle password visibility. Only meaningful when type="password". */
  showPasswordToggle?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, id, showPasswordToggle, type, ...props }, ref) => {
    // Fall back to a stable generated id when the caller passes neither `id` nor `name` --
    // without this, `label htmlFor` and `input id` were both undefined and never actually
    // associated (visually adjacent, but invisible to getByLabel/screen readers).
    const generatedId = useId();
    const inputId = id || props.name || generatedId;
    const errorId = error ? `${inputId}-error` : undefined;
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const resolvedType =
      showPasswordToggle && type === 'password'
        ? isPasswordVisible ? 'text' : 'password'
        : type;

    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
            {/* Strip trailing ' *' from label string; render red asterisk via props.required */}
            {props.required ? (
              <>
                {label.replace(/\s*\*$/, '')}
                <span className="ml-0.5 text-red-500" aria-hidden="true"> *</span>
              </>
            ) : label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={cn(
              'block w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300',
              showPasswordToggle && type === 'password' ? 'pr-10' : '',
              className
            )}
            aria-invalid={!!error}
            aria-describedby={errorId}
            {...props}
          />
          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              onClick={() => setIsPasswordVisible((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {isPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
        {error && (
          <p id={errorId} className="text-sm text-red-500" role="alert">{error}</p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
